import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import clsx from "clsx";
import { useMemo, useState } from "react";

import {
  flattenConversationPages,
  useChatConversations,
  useFeatureflags,
  useOrderMutation,
  useOrderReports,
  useSessionUser,
} from "@web-app/api";
import { SelectInput } from "@web-app/components";
import ChatIcon from "@web-app/pages/chatPage/icons/ChatIcon";
import { focusOnOrder } from "@web-app/pages/common/DispatchMap/mapFocusUtils";
import useOrderSidebar from "@web-app/pages/common/OrdersSidebar/useOrderSidebar";
import {
  capitalizeWords,
  generateSearchRegex,
  getSearchFieldsExceptOrderCard,
} from "@web-app/pages/common/OrdersSidebar/utils";
import useDispatchStore from "@web-app/store/dispatchStore";
import {
  BaseApp,
  Order,
  OrderDeliveryType,
  OrderPriorityType,
  Route,
  SystemApp,
  User,
  Workspace,
  WorkspaceConnection,
} from "@web-app/types";
import {
  getMetersToMiles,
  isActiveOrderDelayed,
  isEarlyDeliveredOrder,
} from "@web-app/utils/helpers";
import { ORDER_STATUS_COLORS, hasExternalAssignee } from "@web-app/utils/order";

import Avatar from "../Avatar";
import Button from "../Button";
import CardPill from "../CardPill";
import ConfirmModal from "../ConfirmModal";
import { type ContextMenuItemProps } from "../ContextMenu";
import DateTimeTz from "../DateTimeTz";
import HighlightString from "../HighlightString";
import { Icon } from "../Icon";
import { InfoBox } from "../InfoBox";
import Modal from "../Modal";
import OrderETAFooter from "../OrderETAFooter";
import SendToMenu, { ContextMenuItem } from "../SendToMenu";
import Skeleton from "../Skeleton";
import Tooltip from "../Tooltip";

import { useExternalAssignee } from "./utils";

const STATUS_BACKGROUND_MAP: Record<
  // @ts-ignore need to fix
  ReturnType<Order["getStatusText"]>,
  string
> = {
  "": "bg-gray-200",
  Assigned: "bg-purple-400",
  Cancelled: "bg-red-300",
  Completed: "bg-green-400",
  Failed: "bg-red-300",
  "In Transit": "bg-sky-300",
  "In Transit to Drop-off": "bg-sky-600",
  "In Transit to Pickup": "bg-sky-300",
  Unassigned: "bg-gray-200",
};

const STATUS_BORDER_MAP = {
  "": "border-gray-500",
  Assigned: "border-purple-400",
  Cancelled: "border-red-300",
  Completed: "border-green-400",
  Failed: "border-red-300",
  "In Transit": "border-sky-300",
  "In Transit to Drop-off": "border-sky-600",
  "In Transit to Pickup": "border-sky-300",
  Unassigned: "border-gray-500",
};

export type DoordashQuotation = {
  dasher_id: string;
  dasher_name: string;
  delivery_status: string;
  dropoff_time_estimated: string;
  external_delivery_id: string;
  fee: number;
  message: string;
  pickup_time_estimated: string;
  tracking_url: string;
};

function ExternalAssigneeLogo({ type }: { type: string }) {
  if (!type) return null;
  return <img src={`/images/${type}.png`} alt={type} className="w-[16px]" />;
}

export default function OrderCard({
  app,
  assignee,
  className,
  isAppsLoading,
  isAssigneesLoading,
  isAutoAssignSearching,
  onShare,
  onViewDetails,
  order,
  removeOrder,
  route,
  routeIndex,
  searchTerm,
  workspaceConnections,
  onSelectExternalProvider,
  routedOrdersDraftEstimatedETA = new Map(),
}: {
  app?: BaseApp | null;
  assignee?: User | null;
  className?: string;
  isAppsLoading?: boolean;
  isAssigneesLoading?: boolean;
  isAutoAssignSearching?: boolean;
  onSelectExternalProvider?: (
    entity: Order | Route | null,
    provider: string,
    type: "order" | "route",
  ) => void;
  onShare?: (workspaceId: string) => void;
  onViewDetails?: () => void;
  order: Order;
  removeOrder?: (orderId: string) => void;
  route?: Route | null;
  routeIndex?: number | null;
  routedOrdersDraftEstimatedETA?: Map<string, Date | null>;
  searchTerm?: string;
  workspaceConnections?: WorkspaceConnection[];
}) {
  const contextMenuVisible = onViewDetails;

  const { deleteOrderById } = useOrderMutation();
  const [isShareModalVisible, setIsShareModalVisible] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(
    order.connectedWorkspaceId ?? "",
  );
  const { mapFocusedOrderId, focusedRouteId } = useDispatchStore();
  const { data: currentUser } = useSessionUser();
  const reports = useOrderReports(order.id);
  const { data: conversationsData } = useChatConversations();
  const conversations = flattenConversationPages(conversationsData);
  const { hasFeature } = useFeatureflags();
  const hasConversations = hasFeature("conversations");

  const searchFieldsExceptOrderCard = getSearchFieldsExceptOrderCard(order);

  const matchesSearch = useMemo(() => {
    const s = new Set<string>();
    const searchTermRegex = generateSearchRegex(searchTerm || "");
    for (const field in searchFieldsExceptOrderCard) {
      const value = searchFieldsExceptOrderCard[field];
      if (searchTerm && value && searchTermRegex.test(value)) {
        s.add(field);
      }
    }
    return s;
  }, [searchTerm, searchFieldsExceptOrderCard]);

  const workspaceConnection = workspaceConnections?.find(
    (conn) =>
      conn.workspaceId == order.workspaceId ||
      conn.connectedWorkspaceId == order.workspaceId,
  );

  const workspace = workspaceConnection
    ? workspaceConnection.workspaceId == order.workspaceId
      ? workspaceConnection.workspace
      : workspaceConnection.connectedWorkspace
    : currentUser?.currentWorkspace;

  let connectedWorkspace: Workspace | undefined;

  if (order.connectedWorkspaceId) {
    const connectedWorkspaceConnection = workspaceConnections?.find(
      (conn) =>
        conn.workspaceId == order.connectedWorkspaceId ||
        conn.connectedWorkspaceId == order.connectedWorkspaceId,
    );

    connectedWorkspace = connectedWorkspaceConnection
      ? connectedWorkspaceConnection.connectedWorkspaceId ==
        order.connectedWorkspaceId
        ? connectedWorkspaceConnection.connectedWorkspace
        : connectedWorkspaceConnection.workspace
      : undefined;
  }

  const { onCopy, onDelete } = useOrderSidebar();

  const [orderToDelete, setOrderToDelete] = useState<string | null>(null);
  const [orderToDuplicate, setOrderToDuplicate] = useState<string | null>(null);

  const handleShareOrder = () => {
    if (onShare) {
      onShare(selectedWorkspaceId);
    }
    setIsShareModalVisible(false);
  };

  const draftEstimatedETA = routedOrdersDraftEstimatedETA.get(order.id)
    ? routedOrdersDraftEstimatedETA.get(order.id)
    : null;

  const openExternalProviderModal = (provider: string) => {
    if (!onSelectExternalProvider) return;
    onSelectExternalProvider(order, provider, "order");
  };

  const { externalAssigneeType, showExternalAssigneeLogo } =
    useExternalAssignee(order, route);
  const orderStatus =
    order.status === "TODO"
      ? order?.assigneeId
        ? "ASSIGNED"
        : "UNASSIGNED"
      : order.status;

  const handleClickCrosshair = () => {
    if (mapFocusedOrderId === order.id) {
      useDispatchStore.setState({ mapFocusedOrderId: null });
      return;
    }

    if (focusedRouteId === order.routeId) {
      useDispatchStore.setState({
        mapFocusedOrderId: order.id,
        mapFocusedRouteId: null,
      });
    } else {
      useDispatchStore.setState({
        focusedRouteId: null,
        mapFocusedOrderId: order.id,
      });
    }

    setTimeout(() => {
      const freshMapRef = useDispatchStore.getState().dispatchMapRef;
      if (freshMapRef?.current) {
        focusOnOrder({
          mapRef: freshMapRef.current,
          order,
        });
      }
    }, 200);
  };

  const getCardHoverTitle = () => {
    return `The order belongs to ${workspace?.name}${connectedWorkspace ? ` and sent to ${connectedWorkspace.name} for delivery` : ``}`;
  };

  const onDeleteOrder = async (orderId: string) => {
    try {
      await onDelete(orderId);
      useDispatchStore.setState({ focusedOrderId: null });
      // Close the modal after successful deletion
      setOrderToDelete(null);
    } catch (error) {
      // Modal stays open if deletion fails
      console.error("Failed to delete order:", error);
    }
  };

  const handleReportIssue = () => {
    useDispatchStore.setState({
      focusedOrderId: order.id,
      focusedRouteId: null,
      isEditOrder: false,
      mapFocusedOrderId: order.id,
      reportIssueOrderId: order.id,
    });
  };

  const menuItems: ContextMenuItemProps[] = [
    {
      disabled: false,
      label: "See Details",
      onClick: onViewDetails ?? (() => {}),
    },
    {
      disabled: false,
      icon: "fasPencil",
      label: "Edit",
      onClick: () => {
        useDispatchStore.setState({
          focusedOrderId: order.id,
          isEditOrder: true,
          mapFocusedOrderId: order.id,
        });
      },
    },
    {
      disabled: false,
      icon: "copy",
      label: "Duplicate",
      onClick: () => setOrderToDuplicate(order.id),
    },
    {
      disabled: !!externalAssigneeType,
      disabledTooltipMessage: externalAssigneeType
        ? "Orders assigned to an external dsp can't be deleted. Please use the cancel option instead"
        : undefined,
      icon: "trash",
      label: "Delete",
      onClick: () => {
        setOrderToDelete(order.id);
      },
    },
  ];

  const orderMayBeDelayed = isActiveOrderDelayed(order, draftEstimatedETA);
  const isEarlyDelivery = useMemo(() => {
    return isEarlyDeliveredOrder(order, draftEstimatedETA);
  }, [order, draftEstimatedETA]);

  const isPublicOrder = app?.systemApp === SystemApp.PUBLIC_FORM;

  return (
    <div
      className={clsx(
        "relative flex flex-col w-full divide-y group rounded-md text-gray-600 transition ease-in bg-gray-50 border",
        `hover:${STATUS_BORDER_MAP[order.statusText]}`,
        className,
        orderMayBeDelayed ? "!border-red-500" : "",
        isEarlyDelivery ? "!border-blue-500" : "",
        [
          mapFocusedOrderId === order.id
            ? STATUS_BORDER_MAP[order.statusText]
            : "border-gray-200",
        ],
      )}
    >
      <div className="flex">
        <div className="my-2 flex w-full">
          <div
            className={clsx(
              "w-1 rounded-r-xl",
              STATUS_BACKGROUND_MAP[order.statusText],
            )}
          />
          <div className="w-[calc(100%-5px)] px-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center flex-shrink-0">
                {routeIndex && (
                  <div className="mr-1 flex">
                    <Icon
                      name="route"
                      className="text-gray-400 w-3 lg:w-4 mr-1"
                    />
                    <div className="text-gray-600 text-[10px] px-1 lg:text-xs bg-gray-50 rounded-full flex justify-center items-center">
                      {routeIndex}
                    </div>
                  </div>
                )}

                {order.priority === OrderPriorityType.HIGH && (
                  <Icon
                    name="flag"
                    className={clsx(
                      "mr-1 w-3 lg:w-4",
                      orderStatus && ORDER_STATUS_COLORS[orderStatus].text,
                    )}
                  />
                )}

                <CardPill
                  className={clsx(
                    orderStatus &&
                      ORDER_STATUS_COLORS[orderStatus].card.darkBackground,
                    // "only visible from lg breakpoint"
                    "hidden md:inline-block",
                  )}
                  text={order.statusText}
                />

                {reports.length > 0 && (
                  <CardPill
                    text="Reported"
                    className="bg-red-700 ml-[3px] lg:ml-[4px]"
                  />
                )}

                {isAppsLoading && order.appId ? (
                  <Skeleton
                    height="h-4"
                    width="w-20"
                    className="rounded ml-[4px]"
                  />
                ) : (
                  isPublicOrder && (
                    <CardPill
                      text="External Order"
                      icon="falFileImport"
                      className="border border-gray-200 !text-black bg-white ml-[4px]"
                    />
                  )
                )}

                {order.status === "COMPLETE" && order.driverRouteDistance ? (
                  <p className="text-gray-600 text-[10px] ml-1">
                    {getMetersToMiles(order.driverRouteDistance)} miles
                  </p>
                ) : order.estimatedDistance ? (
                  <p className="text-gray-600 text-[10px] ml-1">
                    {getMetersToMiles(order.estimatedDistance)} miles
                  </p>
                ) : (
                  <></>
                )}
              </div>
              <div
                className={clsx(
                  "flex items-center gap-1 md:gap[6px]",
                  removeOrder && "mr-4",
                )}
              >
                {order.externalId ? (
                  <div className="flex items-center text-[10px] gap-1 md:gap[6px]">
                    <Icon
                      name="arrowTurnDownRight"
                      className="w-3 text-gray-300"
                    />{" "}
                    {app?.avatarUrl && (
                      <img
                        src={app.avatarUrl}
                        className="block"
                        height={16}
                        width={16}
                        alt=""
                      />
                    )}
                    <HighlightString
                      text={order.externalId}
                      searchTerm={searchTerm}
                    />
                  </div>
                ) : (
                  <div className="flex items-center text-[10px] gap-1 md:gap[6px]">
                    <HighlightString
                      text={order.displayId}
                      searchTerm={searchTerm}
                    />
                  </div>
                )}
                <Icon name="arrowRight" className="text-gray-300 text-xs" />
                <div
                  className="flex items-center gap-1 md:gap[6px]"
                  title={getCardHoverTitle()}
                >
                  {workspace && (
                    <div className="flex items-center">
                      {workspace.avatarUrl ? (
                        <img
                          src={workspace.avatarUrl}
                          className="h-4 block"
                          height={16}
                          width={16}
                          alt=""
                        />
                      ) : (
                        <Avatar image="" name={workspace.name || ""} size={6} />
                      )}
                    </div>
                  )}
                  {connectedWorkspace &&
                    (connectedWorkspace.name ||
                      connectedWorkspace.avatarUrl) && (
                      <div className="flex items-center gap-1 md:gap[6px]">
                        <Icon
                          name="arrowRight"
                          className="text-xs text-gray-300"
                        />
                        {connectedWorkspace.avatarUrl ? (
                          // TODO: FIXME: Need image optimization here
                          <img
                            src={connectedWorkspace.avatarUrl}
                            className="h-4 block"
                            height={16}
                            width={16}
                            alt=""
                          />
                        ) : (
                          <Avatar
                            image=""
                            name={connectedWorkspace.name || ""}
                            size={8}
                          />
                        )}
                      </div>
                    )}
                  {showExternalAssigneeLogo && (
                    <ExternalAssigneeLogo type={externalAssigneeType} />
                  )}
                </div>
                <div className="flex items-center">
                  {!removeOrder && (
                    <div
                      className={clsx(
                        "rounded-md h-6 w-6 hover:bg-gray-200 text-gray-600 flex items-center justify-center cursor-pointer",
                        mapFocusedOrderId === order.id && "bg-gray-200",
                      )}
                      onClick={handleClickCrosshair}
                    >
                      <Icon name="locationCrossHairs" className="h-3" />
                    </div>
                  )}
                  {contextMenuVisible && (
                    <Menu as="div">
                      {({ close }) => (
                        <>
                          <MenuButton
                            className="right cursor-pointer rounded-md transition ease-in hover:bg-gray-200 flex items-center"
                            data-testid="cy-order-context-menu-trigger"
                          >
                            <Icon
                              name="ellipsis"
                              className="h-3 lg:h-4 w-3 lg:w-4 p-1 mx-auto"
                            />
                          </MenuButton>
                          <MenuItems
                            anchor="bottom end"
                            className="z-50 min-w-max origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none transition ease-out duration-100 data-[closed]:opacity-0 data-[closed]:scale-95"
                          >
                            {menuItems.map((item) =>
                              item.disabled && item.disabledTooltipMessage ? (
                                <Tooltip
                                  key={item.label}
                                  position="left"
                                  showIcon={false}
                                  text={item.disabledTooltipMessage}
                                >
                                  <MenuItem
                                    as={ContextMenuItem}
                                    disabled={item.disabled}
                                    onClick={item.onClick}
                                  >
                                    {item.label}
                                  </MenuItem>
                                </Tooltip>
                              ) : (
                                <MenuItem
                                  key={item.label}
                                  as={ContextMenuItem}
                                  disabled={item.disabled}
                                  onClick={item.onClick}
                                >
                                  {item.label}
                                </MenuItem>
                              ),
                            )}
                            <SendToMenu
                              entity={{
                                externalAssigneeId:
                                  order.externalAssigneeId ?? "",
                                id: order.id,
                                numOfOrders: 1,
                                type: order.routeId
                                  ? ""
                                  : order.type ===
                                      OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF
                                    ? "P2P_ORDER"
                                    : order.type
                                      ? "ORDER"
                                      : "",
                              }}
                              onShare={onShare}
                              openExternalProviderModal={
                                openExternalProviderModal
                              }
                              setIsShareModalVisible={setIsShareModalVisible}
                              filteredWorkspaceConnections={
                                workspaceConnections
                              }
                              close={close}
                              workspace={workspace}
                              connectedWorkspace={connectedWorkspace}
                            />
                            <MenuItem
                              as={ContextMenuItem}
                              onClick={handleReportIssue}
                            >
                              Report Issue
                            </MenuItem>
                          </MenuItems>
                        </>
                      )}
                    </Menu>
                  )}
                </div>
              </div>
            </div>
            <div className="my-2.5 flex flex-col gap-[4px] text-[10px] lg:text-xs text-gray-900">
              {order.pickup?.address && (
                <span className="truncate text-gray-600">
                  <Icon
                    name="rightFromBracket"
                    className="mr-2 text-gray-400"
                  />
                  <HighlightString
                    text={`${order.pickup.name ? order.pickup.name + ", " : ""}${order.pickup.address}`}
                    searchTerm={searchTerm}
                  />
                </span>
              )}
              {order.dropoff?.address && (
                <span className="truncate text-gray-600">
                  <Icon name="rightToBracket" className="mr-2 text-gray-400" />
                  <HighlightString
                    text={`${
                      order?.dropoff?.name && order?.dropoff?.name + ", "
                    }${order.dropoff.address}`}
                    searchTerm={searchTerm}
                  />
                </span>
              )}
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] lg:text-xs">
              <div className="flex items-center gap-2 ">
                {isAssigneesLoading && order.assigneeId ? (
                  <span className="flex items-center gap-2">
                    <Skeleton
                      height="h-6"
                      width="w-6"
                      className="rounded-full"
                    />
                    <Skeleton height="h-3" width="w-16" className="rounded" />
                  </span>
                ) : assignee ? (
                  <span className="flex items-center gap-2">
                    <Avatar
                      image={assignee.image ? assignee.image : ""}
                      name={assignee?.name || ""}
                      size={6}
                    />
                    <span className="w-10 md:w-20 truncate">
                      <HighlightString
                        text={assignee?.name}
                        searchTerm={searchTerm}
                      />
                    </span>
                    {hasConversations && !hasExternalAssignee(order) && (
                      <button
                        className="cursor-pointer hover:opacity-80 hidden lg:block transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!order.assigneeId || !assignee) return;

                          const existingConversation = conversations.find(
                            (conv) =>
                              conv.conversation.orderId === order.id &&
                              conv.participantsWithDetails.some(
                                (p) => p.userId === order.assigneeId,
                              ),
                          );

                          if (existingConversation) {
                            useDispatchStore.setState({
                              activeChatConversationId:
                                existingConversation.conversation.id,
                              activeChatDriverId: order.assigneeId,
                              isChatPanelOpen: true,
                              pendingChatDriverImage: null,
                              pendingChatDriverName: null,
                              pendingChatOrderId: null,
                              pendingChatUserId: null,
                            });
                          } else {
                            useDispatchStore.setState({
                              activeChatConversationId: null,
                              activeChatDriverId: order.assigneeId,
                              isChatPanelOpen: true,
                              pendingChatDriverImage: assignee.image || null,
                              pendingChatDriverName: assignee.name,
                              pendingChatOrderDisplayId: order.displayId,
                              pendingChatOrderId: order.id,
                              pendingChatUserId: order.assigneeId,
                            });
                          }
                        }}
                      >
                        <div className="bg-white border border-gray-300 rounded-md p-1 shadow-sm">
                          <ChatIcon className="w-3 h-3" />
                        </div>
                      </button>
                    )}
                  </span>
                ) : isAutoAssignSearching ? (
                  <span className="flex items-center gap-2 text-blue-500">
                    <Icon name="spinner" className="h-4 w-4 animate-spin" />
                    <span className="text-[10px] lg:text-xs">Searching...</span>
                  </span>
                ) : null}
              </div>
              {draftEstimatedETA ? (
                <span
                  className={clsx({
                    "text-blue-500": isEarlyDelivery,
                    "text-red-500": orderMayBeDelayed,
                  })}
                >
                  <span
                    className={clsx("mr-1", !isEarlyDelivery && "invisible")}
                  >
                    <Icon name="chevronsUp" />
                  </span>
                  ETA{" "}
                  <DateTimeTz
                    presentation="eta"
                    start={draftEstimatedETA}
                    timezone={order.timezone}
                  />
                </span>
              ) : (
                <OrderETAFooter
                  order={order}
                  orderMayBeDelayed={orderMayBeDelayed}
                  isEarlyDeliveryOrder={!!isEarlyDelivery}
                />
              )}
              <DateTimeTz
                start={order.scheduledStartDateTime}
                end={order.scheduledEndDateTime}
                timezone={order?.timezone}
                presentation="flat"
              />
            </div>
            {order.status === "INPROGRESS" && order.eta?.progress !== null && (
              <div className="mt-2 h-1 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className={clsx(
                    "h-1 rounded-full",
                    STATUS_BACKGROUND_MAP[order.statusText],
                  )}
                  style={{ width: `${order.eta?.progress}%` }}
                ></div>
              </div>
            )}
          </div>
        </div>

        {removeOrder && (
          <div
            className="invisible absolute right-1 top-1 cursor-pointer group-hover:visible"
            onClick={(e) => {
              e.stopPropagation();
              removeOrder(order.id);
            }}
          >
            <Icon
              name="circleXMark"
              className="h-4 w-4 bg-gray-900 rounded-full text-white font-normal"
            />
          </div>
        )}
      </div>

      <Modal
        isOpen={isShareModalVisible}
        onClose={() => setIsShareModalVisible(false)}
        title="Send Order"
        actions={[
          <Button type="button" onClick={handleShareOrder}>
            Send
          </Button>,
          <Button
            type="button"
            plain
            onClick={() => setIsShareModalVisible(false)}
          >
            Cancel
          </Button>,
        ]}
      >
        <div className="flex flex-col gap-2 w-full px-3 pb-4">
          <InfoBox message="Sending this order to another workspace will allow the order to be completed by the chosen workspace." />
          <SelectInput
            options={
              workspaceConnections?.map((connection) => ({
                label: connection.connectedWorkspace.name,
                value: connection.connectedWorkspace.id,
              })) ?? []
            }
            onChange={(value) => setSelectedWorkspaceId(value)}
            placeholder="Select a workspace"
            selected={selectedWorkspaceId}
          />
        </div>
      </Modal>
      {orderToDelete && (
        <ConfirmModal
          title={
            <div className="flex items-center gap-4">
              <Icon
                name="fasCircleExclamation"
                className="w-6 h-6 text-red-500"
              />
              <span>Delete Order</span>
            </div>
          }
          subtitle="Are you sure you want to delete this order?"
          isOpen={!!orderToDelete}
          actions={[
            {
              color: "red",
              isLoading: deleteOrderById.isPending,
              label: "Confirm",
              onClick: () => onDeleteOrder(orderToDelete),
              size: "sm",
            },
            {
              label: "Cancel",
              onClick: () => setOrderToDelete(null),
              plain: true,
              size: "sm",
            },
          ]}
          onClose={() => setOrderToDelete(null)}
        />
      )}
      {orderToDuplicate && (
        <ConfirmModal
          title={
            <div className="flex items-center gap-4">
              <Icon
                name="fasCircleExclamation"
                className="w-6 h-6 text-yellow-500"
              />
              <span>Duplicate Order</span>
            </div>
          }
          subtitle="Auto-dispatch logic will be applied to the duplicated order. Do you want to proceed?"
          isOpen={!!orderToDuplicate}
          actions={[
            {
              label: "Confirm",
              onClick: () => {
                onCopy(orderToDuplicate);
                setOrderToDuplicate(null);
              },
              size: "sm",
            },
            {
              label: "Cancel",
              onClick: () => setOrderToDuplicate(null),
              plain: true,
              size: "sm",
            },
          ]}
          onClose={() => setOrderToDuplicate(null)}
        />
      )}
      {searchTerm && matchesSearch.size > 0 && (
        <div className=" bg-gray-50  rounded-bl-md rounded-br-md px-2 py-0.5">
          <ExtraSearchFieldsResult
            matchesSearch={matchesSearch}
            searchFields={searchFieldsExceptOrderCard}
            searchTerm={searchTerm}
          />
        </div>
      )}
    </div>
  );
}

const ExtraSearchFieldsResult = ({
  matchesSearch,
  searchFields,
  searchTerm,
}: {
  matchesSearch: Set<string>;
  searchFields: Record<string, string>;
  searchTerm: string;
}) => {
  const generateSearchResultText = () => {
    let resultText = "Found ";

    for (const [index, item] of [...matchesSearch].entries()) {
      const data = searchFields[item];
      const separator = index === matchesSearch.size - 1 ? "." : ", ";

      resultText += `"${capitalizeWords(item.split("_").join(" "))}: ${data}"${separator} `;
    }
    return <HighlightString text={resultText} searchTerm={searchTerm} />;
  };

  return (
    <div>
      <Icon name="magnifyingGlass" className="h-3 mr-1 text-gray-500" />
      <span className="text-xs">{generateSearchResultText()}</span>
    </div>
  );
};
